import {LitElement, html} from 'https://cdn.jsdelivr.net/gh/lit/dist@3/core/lit-core.min.js';
import './file-browser.js';
import { handleDropItems, downloadItems, pasteItems, setClipboardCopy, setClipboardCut, clearClipboard, moveItemsToDir } from '../utils/file-ops.js';
import { uploadFiles } from '../api/fs.js';
import { apiUpload } from '../api/client.js';
import { showToast, showUploadToast, showMoveToDialog, formatSize } from '../utils/ui.js';
import { classifyFile } from '../utils/file-types.js';

export class FileBrowserLogic extends LitElement {
    static properties = {
        currentPath: { type: String },
        active: { type: Boolean },
        viewMode: { type: String }
    };

    // Use Light DOM - styles come from style.css
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.currentPath = '/';
        this.active = false;
        this.viewMode = 'list';
        
        if (window.appState) {
            this.currentPath = window.appState.rootDir || window.appState.currentPath || '/';
            this.viewMode = window.appState.viewMode || 'list';
        }
    }
    
    updated(changedProperties) {
        if (changedProperties.has('currentPath')) {
            if (window.appState) window.appState.currentPath = this.currentPath;
            this._refreshBrowser();
            this.dispatchEvent(new CustomEvent('path-changed', { 
                bubbles: true, 
                composed: true, 
                detail: { path: this.currentPath } 
            }));
        }
        if (changedProperties.has('viewMode')) {
             if (window.appState) window.appState.viewMode = this.viewMode;
             this._refreshBrowser(); // Re-render with new mode
        }
    }

    render() {
        // Light DOM - renders directly, file-browser also uses Light DOM
        return html`
            <file-browser
                .currentPath=${this.currentPath}
                .viewMode=${this.viewMode}
                @navigate=${this._handleNavigate}
                @open-file=${this._handleOpenFile}
                @upload-drop=${this._handleUploadDrop}
                @selection-changed=${this._handleSelectionChanged}
                @clipboard-action=${this._handleClipboardAction}
                @internal-move=${this._handleInternalMove}
            ></file-browser>
        `;
    }

    /* Public Methods for External Controls */
    
    refresh() {
        this._refreshBrowser();
    }

    /**
     * Force reload the file list (even if path hasn't changed).
     * Used after machine switch to reload at the same path.
     */
    reload() {
        this._refreshBrowser();
    }

    setViewMode(mode) {
        this.viewMode = mode;
    }

    createFolder() {
        // Light DOM: use this.querySelector instead of shadowRoot
        const browser = this.querySelector('file-browser');
        if (browser) browser.startCreation('folder');
    }

    createFile() {
        const browser = this.querySelector('file-browser');
        if (browser) browser.startCreation('file');
    }

    triggerUpload() {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.onchange = async () => {
            if (input.files.length > 0) {
                const toast = showUploadToast();
                try {
                    await uploadFiles(this.currentPath, input.files, (percent, metrics) => {
                        toast.update(percent, metrics);
                    });
                    toast.finish(true, 'Upload complete');
                    this.refresh();
                } catch (e) {
                    toast.finish(false, e.message || 'Upload failed');
                }
            }
        };
        input.click();
    }

    triggerFolderUpload() {
        const input = document.createElement('input');
        input.type = 'file';
        input.setAttribute('webkitdirectory', '');
        input.onchange = async () => {
            if (!input.files || input.files.length === 0) {
                showToast('No files found to upload', 'warning');
                return;
            }
            const toast = showUploadToast();
            try {
                // Build path-preserving file list from webkitRelativePath
                const filesWithPaths = Array.from(input.files).map(file => ({
                    file,
                    name: file.webkitRelativePath || file.name,
                }));
                await apiUpload(this.currentPath, filesWithPaths, (percent, metrics) => {
                    toast.update(percent, metrics);
                });
                toast.finish(true, 'Folder upload complete');
                this.refresh();
            } catch (e) {
                toast.finish(false, `Folder upload failed: ${e.message}`);
            }
        };
        input.click();
    }
    
    triggerDelete() {
        const browser = this.querySelector('file-browser');
        if (browser) browser._deleteSelected();
    }
    
    triggerDownload() {
        const selectedPaths = this._getSelectedFullPaths();
        if (selectedPaths.length > 0) {
            downloadItems(selectedPaths);
        }
    }
    
    triggerCopy() {
        const items = this._getSelectedItems();
        if (items.length > 0) {
            setClipboardCopy(items);
        }
    }

    triggerCopyPath() {
        const selectedPaths = this._getSelectedFullPaths();
        if (selectedPaths.length === 0) return;
        const text = selectedPaths.join('\n');

        // Use a textarea+execCommand fallback — the async clipboard API loses
        // the user-gesture context through Lit's event dispatch chain.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();

        let ok = false;
        try {
            ok = document.execCommand('copy');
        } catch (_) { /* ignored */ }
        document.body.removeChild(ta);

        if (ok) {
            showToast(
                selectedPaths.length === 1 ? 'Path copied to clipboard' : `${selectedPaths.length} paths copied to clipboard`,
                'success');
        } else {
            showToast('Failed to copy path', 'error');
        }
    }

    triggerCut() {
        const items = this._getSelectedItems();
        if (items.length > 0) {
            setClipboardCut(items);
            // Add visual indicator for cut items (markCutItems still expects plain paths)
            const browser = this.querySelector('file-browser');
            if (browser) browser.markCutItems(items.map(i => i.path));
        }
    }
    
    async triggerPaste() {
        const success = await pasteItems(this.currentPath);
        if (success) {
            // Remove cut visual indicators and refresh
            const browser = this.querySelector('file-browser');
            if (browser) {
                browser.clearCutMarkers();
                browser.loadFiles();
            }
        }
    }
    
    triggerRename() {
        const browser = this.querySelector('file-browser');
        if (browser && window.appState.selectedFiles.size === 1) {
            const fileName = Array.from(window.appState.selectedFiles)[0];
            browser._startRename(fileName);
        }
    }

    async triggerMoveTo() {
        const selectedPaths = this._getSelectedFullPaths();
        if (selectedPaths.length === 0) return;
        const destDir = await showMoveToDialog(this.currentPath);
        if (!destDir) return;
        try {
            const { renameItem } = await import('../api/fs.js');
            for (const sourcePath of selectedPaths) {
                const fileName = sourcePath.split('/').pop();
                const cleanDest = destDir === '/' ? '' : destDir.replace(/\/+$/, '');
                const destPath = cleanDest ? `${cleanDest}/${fileName}` : `/${fileName}`;
                if (sourcePath === destPath) continue;
                await renameItem(sourcePath, destPath);
            }
            showToast(`Moved ${selectedPaths.length} item(s)`, 'success');
            this.refresh();
        } catch (err) {
            showToast(`Move failed: ${err.message}`, 'error');
        }
    }

    navigateUp() {
        const rootDir = window.appState.rootDir;
        if (rootDir && this.currentPath === rootDir) return;
        if (this.currentPath === '/') return;
        const parts = this.currentPath.split('/').filter(p => p);
        parts.pop();
        const nextPath = parts.length === 0 ? '/' : '/' + parts.join('/');
        if (rootDir && nextPath.length < rootDir.length && rootDir.startsWith(nextPath)) {
            return;
        }
        this.currentPath = nextPath;
    }
    
    _getSelectedFullPaths() {
        if (!window.appState.selectedFiles || window.appState.selectedFiles.size === 0) {
            return [];
        }
        return Array.from(window.appState.selectedFiles);
    }

    /**
     * Get selected items as {path, isDir} objects by looking up the file-browser's file list.
     * Clipboard operations use this to avoid server round-trips for type detection.
     */
    _getSelectedItems() {
        const paths = this._getSelectedFullPaths();
        if (paths.length === 0) return [];
        const browser = this.querySelector('file-browser');
        const files = browser?.files || [];
        return paths.map(p => {
            const file = files.find(f => this.currentPath === '/' ? `/${f.name}` === p : `${this.currentPath}/${f.name}` === p);
            return { path: p, isDir: file ? !!file.is_dir : false };
        });
    }

    /* Internal Handlers */

    _refreshBrowser() {
        const browser = this.querySelector('file-browser');
        if (browser) browser.loadFiles();
    }

    _handleNavigate(e) {
        this.currentPath = e.detail.path;
    }

    async _handleUploadDrop(e) {
        const items = e.detail.items;
        await handleDropItems(items, this.currentPath);
        this.refresh();
    }

    _handleOpenFile(e) {
        const { file, path } = e.detail;
        const fileClass = classifyFile(file.name);

        // Media, PDF, Markdown, HTML -> unified overlay viewer
        if (fileClass === 'media' || fileClass === 'pdf' || fileClass === 'markdown' || fileClass === 'html') {
            const viewer = document.querySelector('overlay-viewer');
            if (viewer) {
                // Media needs fileList + currentDir for prev/next navigation.
                // PDF and Markdown don't use them but accept the params harmlessly.
                const browser = this.querySelector('file-browser');
                const fileList = browser
                    ? (browser.searchMode === 'api' ? browser._displayFiles : browser.files)
                    : [];
                const currentDir = (browser?.searchMode === 'api') ? null : this.currentPath;
                viewer.open(path, fileList, currentDir);
            }
            return;
        }

        // Editor - apply size guard
        const MAX_SIZE = 1024 * 1024;
        if (file.size > MAX_SIZE) {
            showToast(`File too large (${formatSize(file.size)}).`, 'warning');
            return;
        }

        this.dispatchEvent(new CustomEvent('app-open-file', {
            bubbles: true,
            composed: true,
            detail: {
                path: path,
                name: file.name,
                size: file.size,
                type: 'file'
            }
        }));
    }
    
    _handleSelectionChanged(e) {
        if (window.appState) {
            window.appState.selectedFiles = new Set(e.detail.selection);
        }
    }
    
    _handleClipboardAction(e) {
        const { operation } = e.detail;
        switch (operation) {
            case 'copy':
                this.triggerCopy();
                break;
            case 'cut':
                this.triggerCut();
                break;
            case 'paste':
                this.triggerPaste();
                break;
        }
    }

    async _handleInternalMove(e) {
        const { paths, destDir } = e.detail;
        if (!paths || paths.length === 0 || !destDir) return;
        const success = await moveItemsToDir(paths, destDir);
        if (success) {
            const browser = this.querySelector('file-browser');
            if (browser) browser.loadFiles();
        }
    }
}

customElements.define('file-browser-logic', FileBrowserLogic);
